"use client";

/**
 * Who the current visitor is, for analytics.
 *
 * Single source of truth, shared by every tracker (page views, CTA clicks,
 * conversions). Two trackers with their own copies of this logic would
 * eventually disagree about when a session rolls over, and silently split one
 * visit into two — which is unfixable after the fact, because the ids are all
 * that tie the events together.
 *
 * Browser-only: every function here touches storage/window, so callers must
 * be inside an effect or an event handler, never a render path. Storage access never
 * throws (see read/write below).
 */

import { decorateProductUrl } from "./productDomains";

const VISITOR_KEY = "hb_visitor_id";
const SESSION_KEY = "hb_session_id";
const SESSION_SEEN_KEY = "hb_session_last_seen";
const UTM_KEY = "hb_utm";
const CAMPAIGN_KEY = "hb_campaign_attr";

/** Industry-standard inactivity window; also what the session RPCs assume. */
const SESSION_IDLE_MS = 30 * 60 * 1000;

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export type Utm = Partial<Record<(typeof UTM_KEYS)[number], string>>;

/**
 * Storage that cannot throw. Safari private mode, blocked site data and some
 * embedded webviews throw on localStorage/sessionStorage access -- and these helpers
 * run inside click handlers *before* the navigation, so a throw here turned
 * "Get Started" into a dead button. On failure the value lives in memory for the
 * page: the visit is still tracked, just not across reloads.
 */
const memory = new Map<string, string>();
type Area = "local" | "session";
function area(a: Area): Storage | null {
  try {
    // The bare globals (same objects as window.*): merely reading them throws where
    // storage is blocked, which the catch turns into "unavailable".
    return a === "local" ? localStorage : sessionStorage;
  } catch {
    return null;
  }
}
// Memory is used only where storage is unavailable -- never as a second copy of a
// working store, or a value cleared from storage would come back from memory.
function read(a: Area, key: string): string | null {
  try {
    const s = area(a);
    if (s) return s.getItem(key);
  } catch {
    /* blocked: fall through to memory */
  }
  return memory.get(`${a}:${key}`) ?? null;
}
function write(a: Area, key: string, value: string): void {
  try {
    const s = area(a);
    if (s) {
      s.setItem(key, value);
      return;
    }
  } catch {
    /* blocked or full: fall through to memory */
  }
  memory.set(`${a}:${key}`, value);
}

/** Non-throwing storage for other client code (see read/write above). */
export const readStorage = read;
export const writeStorage = write;

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Persistent across visits — the "unique visitor" identity. */
export function getVisitorId(): string {
  let id = read("local", VISITOR_KEY);
  if (!id) {
    id = uuid();
    write("local", VISITOR_KEY, id);
  }
  return id;
}

/**
 * Rolls over after 30 minutes of inactivity. localStorage rather than
 * sessionStorage so a second tab continues the same session instead of inventing
 * a new one — duplicate sessions per visitor were an explicit concern.
 *
 * Note this also refreshes the last-seen stamp, so calling it *is* activity. That
 * is intended: a CTA click should keep a session alive exactly as a page view
 * does.
 */
export function getSessionId(): string {
  const now = Date.now();
  const lastSeen = Number(read("local", SESSION_SEEN_KEY) || 0);
  let id = read("local", SESSION_KEY);
  if (!id || !lastSeen || now - lastSeen > SESSION_IDLE_MS) {
    id = uuid();
    write("local", SESSION_KEY, id);
  }
  write("local", SESSION_SEEN_KEY, String(now));
  return id;
}

/**
 * Read-only session id: returns what is stored without creating one or touching
 * the activity stamp. For callers that want to attach a session to an outbound
 * link (cross-domain handoff) without registering a visit of their own.
 */
export function peekSessionId(): string | null {
  return read("local", SESSION_KEY);
}

export function getDeviceType(): string {
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

export function getBrowser(): string {
  const ua = navigator.userAgent;
  // Order matters: Edge and Opera both carry "Chrome", Chrome carries "Safari".
  if (/Edg\//.test(ua)) return "Edge";
  if (/OPR\//.test(ua)) return "Opera";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Safari\//.test(ua)) return "Safari";
  return "Other";
}

/**
 * First-touch attribution for the session.
 *
 * The campaign params only exist on the URL of the landing view; by the second
 * page they are gone. Remember them for the session so every event carries the
 * attribution, and do not let a later param overwrite the original touch.
 */
export function readAndRememberUtm(): Utm {
  const params = new URLSearchParams(window.location.search);
  const fromUrl: Utm = {};
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v) fromUrl[k] = v.slice(0, 255);
  }

  if (Object.keys(fromUrl).length > 0) {
    const existing = read("session", UTM_KEY);
    // A fresh campaign click mid-session is a new first touch only if we have
    // none yet; otherwise the original source keeps the credit.
    if (!existing) write("session", UTM_KEY, JSON.stringify(fromUrl));
    if (!existing) return fromUrl;
    try {
      return JSON.parse(existing) as Utm;
    } catch {
      return fromUrl;
    }
  }

  const stored = read("session", UTM_KEY);
  if (!stored) return {};
  try {
    return JSON.parse(stored) as Utm;
  } catch {
    return {};
  }
}

/** Which email campaign this visit came from (hb_c / hb_s / hb_r). */
export interface CampaignAttribution {
  campaignId?: string;
  stepId?: string;
  subscriberId?: string;
}

/**
 * Remember the campaign ids a campaign email's link carried, for the rest of the visit.
 *
 * The sender puts them on every site link, but the URL only holds them on the landing
 * view: someone who arrives on /pricing and then clicks to /demo would otherwise book
 * as organic. Last click wins -- unlike UTMs, opening a second campaign email is a
 * newer, deliberate touch. Unverified here by design; create-demo-booking checks the
 * ids against a real enrollment and drops anything that doesn't match.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Only uuid-shaped ids: create-demo-booking rejects the whole booking on a malformed one,
// and a remembered value outlives the URL that carried it.
const uuidOrUndefined = (v: string | null | undefined) => (v && UUID_RE.test(v) ? v : undefined);

export function readAndRememberCampaignAttribution(): CampaignAttribution {
  const q = new URLSearchParams(window.location.search);
  const fromUrl: CampaignAttribution = {
    campaignId: uuidOrUndefined(q.get("hb_c")),
    stepId: uuidOrUndefined(q.get("hb_s")),
    subscriberId: uuidOrUndefined(q.get("hb_r")),
  };
  if (fromUrl.campaignId && fromUrl.subscriberId) {
    write("session", CAMPAIGN_KEY, JSON.stringify(fromUrl));
    return fromUrl;
  }
  return peekCampaignAttribution();
}

/** The remembered campaign attribution, without reading the URL. */
export function peekCampaignAttribution(): CampaignAttribution {
  const stored = read("session", CAMPAIGN_KEY);
  if (!stored) return {};
  try {
    const v = JSON.parse(stored) as CampaignAttribution;
    if (!v || typeof v !== "object") return {};
    return {
      campaignId: uuidOrUndefined(v.campaignId),
      stepId: uuidOrUndefined(v.stepId),
      subscriberId: uuidOrUndefined(v.subscriberId),
    };
  } catch {
    return {};
  }
}

/** Stored first-touch UTMs without reading the URL. */
export function peekUtm(): Utm {
  const stored = read("session", UTM_KEY);
  if (!stored) return {};
  try {
    return JSON.parse(stored) as Utm;
  } catch {
    return {};
  }
}

/**
 * A product URL carrying this visit's identity, for the link or window.open that is
 * about to leave the site. Shared by useProductLinkTracking (anchors) and
 * ProductSelectionModal (a <button> + window.open, which the anchor listener cannot
 * see -- so Log In → product → Sign up used to arrive with no hb_sid/hb_vid and every
 * such registration was filed as direct_registration).
 *
 * peek, not get, for the session: following a link should not mint a session or count
 * as activity. No session yet means nothing to attribute to, so the URL is returned
 * unchanged rather than inventing an id the site never recorded a page view against.
 */
export function decorateWithVisitIdentity(url: string): string {
  const sessionId = peekSessionId();
  if (!sessionId) return url;
  return decorateProductUrl(url, { sessionId, visitorId: getVisitorId(), utm: peekUtm() });
}
